import React, { useState, useEffect, useCallback } from 'react';
import { Info } from 'lucide-react';
import { RemoteSupportService } from '../../services/remoteSupportService';
import { useRealtimeRemoteSupport } from '../../hooks/useRealtimeRemoteSupport';
import { Table, Button, SearchBar } from '../ui';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import RemoteSupportNewModal from './RemoteSupportNewModal';
import RemoteSupportDetailsModal from './RemoteSupportDetailsModal';

/**
 * RemoteSupportTable
 *
 * Table for managing remote support clients.
 *
 * Features:
 * - Server-side pagination (20/50/All)
 * - Server-side search with debouncing
 * - Column sorting (all columns)
 * - Column resizing with auto-size
 * - Real-time updates
 * - Row click to view details
 * - New client modal
 */
const RemoteSupportTable = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('companyName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showClientDetailsModal, setShowClientDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Initial load
  useEffect(() => {
    window.scrollTo(0, 0);
    loadClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time client handlers
  const handleClientUpdate = useCallback((updatedClient) => {
    setClients((prev) =>
      prev.map((client) =>
        client.id === updatedClient.id
          ? {
              id: updatedClient.id,
              companyName: updatedClient.company_name,
              dataCollectors: updatedClient.data_collectors || '',
              invoiceDescription: updatedClient.invoice_description || '',
              baseCost: updatedClient.base_cost ? parseFloat(updatedClient.base_cost).toFixed(2) : '',
              additionalCost: updatedClient.additional_cost ? parseFloat(updatedClient.additional_cost).toFixed(2) : '',
              totalCost: updatedClient.total_cost ? parseFloat(updatedClient.total_cost).toFixed(2) : '',
              notes: updatedClient.notes || '',
            }
          : client
      )
    );
  }, []);

  const handleClientInsert = useCallback((newClient) => {
    setClients((prev) => [
      {
        id: newClient.id,
        companyName: newClient.company_name,
        dataCollectors: newClient.data_collectors || '',
        invoiceDescription: newClient.invoice_description || '',
        baseCost: newClient.base_cost ? parseFloat(newClient.base_cost).toFixed(2) : '',
        additionalCost: newClient.additional_cost ? parseFloat(newClient.additional_cost).toFixed(2) : '',
        totalCost: newClient.total_cost ? parseFloat(newClient.total_cost).toFixed(2) : '',
        notes: newClient.notes || '',
      },
      ...prev,
    ]);
  }, []);

  const handleClientDelete = useCallback((deletedClientId) => {
    setClients((prev) => prev.filter((client) => client.id !== deletedClientId));
  }, []);

  // Set up real-time subscription
  useRealtimeRemoteSupport(
    handleClientUpdate,
    handleClientInsert,
    handleClientDelete,
    true
  );

  // Load clients from server
  const loadClients = async (page = currentPage, search = debouncedSearchQuery, size = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      const result = await RemoteSupportService.getPaginatedClients(
        page,
        size,
        sortField,
        sortDirection,
        search
      );

      setClients(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setCurrentPage(result.currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update when sort or search changes
  useEffect(() => {
    if (!loading) {
      setCurrentPage(1);
      loadClients(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection, debouncedSearchQuery]);

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value) return '—';
    return `$${value}`;
  };

  // Handle row click
  const handleRowClick = (client) => {
    setSelectedClient(client);
    setShowClientDetailsModal(true);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadClients(newPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    loadClients(1, debouncedSearchQuery, newSize);
  };

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  // Handle sort events from Table component
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Column definitions
  const columns = [
    {
      id: 'info',
      label: '',
      field: 'id',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      searchable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            minWidth: '36px',
            minHeight: '36px',
            maxWidth: '36px',
            maxHeight: '36px',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
            e.currentTarget.style.color = 'var(--cta-blue)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="View client details"
          aria-label={`View details for ${row.companyName}`}
        >
          <Info size={20} />
        </button>
      ),
    },
    {
      id: 'companyName',
      label: 'Company Name',
      field: 'companyName',
      width: 250,
      minWidth: 150,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'dataCollectors',
      label: 'Data Collectors',
      field: 'dataCollectors',
      width: 200,
      minWidth: 100,
      maxWidth: 350,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'invoiceDescription',
      label: 'Invoice Description',
      field: 'invoiceDescription',
      width: 250,
      minWidth: 150,
      maxWidth: 450,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '—'}
        </div>
      ),
    },
    {
      id: 'baseCost',
      label: 'Base Cost',
      field: 'baseCost',
      width: 120,
      minWidth: 100,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      align: 'right',
      contentType: 'text',
      render: (value) => formatCurrency(value),
    },
    {
      id: 'additionalCost',
      label: 'Additional Cost',
      field: 'additionalCost',
      width: 140,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      resizable: true,
      align: 'right',
      contentType: 'text',
      render: (value) => formatCurrency(value),
    },
    {
      id: 'totalCost',
      label: 'Total Cost',
      field: 'totalCost',
      width: 120,
      minWidth: 100,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      align: 'right',
      contentType: 'text',
      render: (value) => (
        <span style={{ color: 'var(--success)', fontWeight: 'var(--font-semibold)' }}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      id: 'notes',
      label: 'Notes',
      field: 'notes',
      width: 200,
      minWidth: 100,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '—'}
        </div>
      ),
    },
  ];

  if (loading && clients.length === 0) {
    return <LoadingSpinner message="Loading remote support clients..." />;
  }

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ErrorMessage
            message={error}
            onRetry={loadClients}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            Remote Support Clients
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {totalCount} total clients
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={() => setShowNewClientModal(true)}
        >
          + New Client
        </Button>
      </div>

      {/* Table with integrated toolbar */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '85vh',
      }}>
        {/* Search Bar and Auto-resize Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search remote support clients..."
              size="sm"
            />
          </div>
          <Button
            variant={autoResizeEnabled ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
          >
            {autoResizeEnabled ? '✓ ' : ''}Auto-resize
          </Button>
        </div>

        {/* Scrollable Table Container */}
        <div style={{
          backgroundColor: 'var(--dark-surface)',
          overflowX: 'auto',
          overflowY: 'auto',
          flex: 1,
          minWidth: 0,
        }}>
          <Table
            data={clients}
            columns={columns}
            sortable={true}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            searchable={false}
            resizable={true}
            paginated={false}
            autoResizeOnDataChange={autoResizeEnabled}
            showAutoSizeButton={false}
            storageKey="remote-support-table"
            loading={loading}
            emptyMessage={
              searchQuery
                ? 'No clients found matching your search'
                : 'No remote support clients found. Click "New Client" to add your first client.'
            }
            onRowClick={handleRowClick}
            onManualResize={handleManualResize}
            variant="dense"
            stickyHeader={true}
            style={{ border: 'none', borderRadius: '0', overflow: 'visible' }}
          />
        </div>

        {/* Frozen Pagination */}
        {totalCount > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                Page {currentPage} of {totalPages} ({totalCount} total clients)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                  Show:
                </span>
                <Button
                  variant={pageSize === 20 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(20)}
                >
                  20
                </Button>
                <Button
                  variant={pageSize === 50 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(50)}
                >
                  50
                </Button>
                <Button
                  variant={pageSize === 999999 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(999999)}
                >
                  All
                </Button>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </Button>
                    );
                  }

                  return pages;
                })()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Client Modal */}
      {showNewClientModal && (
        <RemoteSupportNewModal
          isOpen={showNewClientModal}
          onClose={() => setShowNewClientModal(false)}
          onClientCreated={() => {
            loadClients();
            setShowNewClientModal(false);
          }}
        />
      )}

      {/* Client Details Modal */}
      {showClientDetailsModal && (
        <RemoteSupportDetailsModal
          client={selectedClient}
          isOpen={showClientDetailsModal}
          onClose={() => {
            setShowClientDetailsModal(false);
            setSelectedClient(null);
          }}
          initialEditMode={false}
          onClientUpdated={() => {
            loadClients();
            setShowClientDetailsModal(false);
            setSelectedClient(null);
          }}
        />
      )}
    </div>
  );
};

export default RemoteSupportTable;
